import { frameId } from "../assets/manifest.ts";
import type { StudioDocument } from "./types.ts";

/**
 * The entries of a sorted pack's studio document whose frames a new version of the pack lacks (ADR-107 E8). An entry
 * is kept when its frame goes, so the edit comes back with the frame; this names what is waiting, for the studio, the
 * Issues list and the build's warnings.
 */

/**
 * One entry whose frame is gone.
 */
export interface LostEdit {
  /** The entry's key: the frame's id, or the animation's first frame. */
  readonly frame: string;
  /** What to call it: the name an edit gave the frame, else the frame's id. */
  readonly label: string;
  /** Whether the entry is a frame's edit or a frame animation. */
  readonly kind: "frame" | "animation";
}

/**
 * Splits a frame id into its tileset and tile.
 *
 * @param key A frame id of a document entry.
 * @returns The tileset's id and the tile.
 */
function partsOf(key: string): { readonly tileset: string; readonly tile: number } {
  const [tileset, , tile] = key.split("/") as [string, string, string];
  return { tileset, tile: Number(tile) };
}

/**
 * The entries of a document whose frame a pack no longer has: a frame edit whose frame is gone, and a frame
 * animation whose first frame or any listed frame is.
 *
 * @param doc The pack's document.
 * @param hasFrame Whether the pack as it is now holds a frame, by frame id.
 * @returns The lost entries, frame edits then animations, each in the order of its frame id strings.
 */
export function lostEditsOf(doc: StudioDocument, hasFrame: (frame: string) => boolean): LostEdit[] {
  const lost: LostEdit[] = [];
  const nameOf = (key: string): string => doc.frames?.[key]?.name ?? key;
  for (const key of Object.keys(doc.frames ?? {}).sort()) {
    if (!hasFrame(key)) {
      lost.push({ frame: key, label: nameOf(key), kind: "frame" });
    }
  }
  for (const key of Object.keys(doc.frameAnimations ?? {}).sort()) {
    const { tileset } = partsOf(key);
    const listed = doc.frameAnimations?.[key]?.frames ?? [];
    if (!hasFrame(key) || listed.some((tile) => !hasFrame(frameId(tileset, null, tile)))) {
      lost.push({ frame: key, label: nameOf(key), kind: "animation" });
    }
  }
  return lost;
}

/**
 * The sentence the studio, the Issues list and the build's warnings give for a lost entry.
 *
 * @param lost The entry.
 * @returns The sentence, without a full stop.
 */
export function lostEditSentence(lost: LostEdit): string {
  return lost.kind === "frame" ? `${lost.label}: the pack no longer has this frame` : `${lost.label}: the pack no longer has every frame of this animation`;
}
